import PopularTag from '@components/OpportunityListing/components/OpporunityCard/PopularTag';
import {
    getOpportunityData,
    getOpportunityFields,
} from '@components/OpportunityListing/utils/helper';
import { isTBillsView } from '@components/OpportunityListing/utils/utils';
import { opportunityClosedStates } from '@helpers/constants';
import {
    OpportunityStateEnum,
    OpportunityTag,
} from '@helpers/enums/opportunity-enums';
import { Image, Text } from '@ui/atoms';
import React from 'react';
import FullSubscribedTag from '@components/OpportunityListing/components/OpporunityCard/FullSubscribedTag';
import { cn } from '@ui/atoms/utils/utils';
import { OpportunityLogo } from '@molecules/Opportunity';
import CornerElementSvg from '@components/OpportunityListing/components/OpporunityCard/CornerElementSvg';

const OpportunityCarouselCard = ({ type, opportunity, handleOnClick }) => {
    const isBonds = !isTBillsView(type);
    const isClosed = opportunityClosedStates.includes(opportunity?.state);
    const opportunityData = getOpportunityData(opportunity, isBonds, false);

    return (
        <div
            className="flex flex-col flex-1 cursor-pointer scale-[85%] sm:scale-100"
            onClick={() => handleOnClick(opportunity)}
        >
            <OpportunityInfo
                isBonds
                opportunityData={opportunityData}
                riskRating={opportunityData.riskRating}
                state={opportunity?.state}
                isClosed={isClosed}
            />
            <OpportunityInfoStrip
                interestPayoutFrequency={
                    opportunityData.interestPayoutFrequency
                }
                isClosed={isClosed}
            />
        </div>
    );
};

const OpportunityInfo = ({
    opportunityData,
    isBonds,
    isUpcoming = false,
    isFestiveDeal = false,
    isAdminDeal = false,
    isClosed,
    state,
    riskRating,
}) => {
    const {
        logo,
        title,
        industryType,
        irr,
        tenure,
        minInvestment,
        tagType,
        tagLabel,
        preOfferIRR,
    } = opportunityData;
    const fields = getOpportunityFields({
        irr,
        tenure,
        minInvestment,
    });
    const bgImage = isFestiveDeal ? 'FestiveOppCardBg' : 'OppCardBg';

    const isFullyRepaidOnTime =
        state === OpportunityStateEnum.PREPAID_AND_CLOSED ||
        state === OpportunityStateEnum.MATURED_AND_CLOSED;

    const titleDisplayCharLength = 45;

    const getTagType = () => {
        if (isFestiveDeal) return OpportunityTag.FESTIVE_DEAL;
        else if (isUpcoming) return OpportunityTag.COMING_SOON;
        else return tagType;
    };
    return (
        <div
            className={cn(
                'relative overflow-hidden border rounded-bl-none rounded-xl border-primary-70 bg-basicWhite h-full',
                {
                    'rounded-bl-xl': isClosed || isUpcoming,
                    'rounded-bl-none': !isClosed && !isUpcoming,
                    'border-primary-500': isAdminDeal && !isClosed,
                    'border-[#D7BC5F]': isFestiveDeal,
                }
            )}
        >
            <Image
                src={`/images/opportunities/${bgImage}.svg`}
                className="absolute top-0 bottom-0 object-cover w-full h-full -left-3 inline-block"
            />
            {isBonds && !isClosed ? (
                <PopularTag
                    type={getTagType()}
                    className="absolute top-0 z-10 left-4"
                    tagLabel={tagLabel}
                />
            ) : null}
            {isClosed ? (
                <FullSubscribedTag
                    isFullyRepaid={isFullyRepaidOnTime}
                    className="absolute top-0 left-0 z-10"
                />
            ) : null}
            <div
                className={cn(
                    'relative z-10 flex flex-col justify-center h-full gap-4 p-4',
                    {
                        'pt-9': isBonds || isClosed,
                    }
                )}
            >
                <div className="flex items-start gap-4 flex-col">
                    <div
                        className={cn('flex flex-col gap-4 flex-1', {
                            'pb-3': isUpcoming,
                        })}
                    >
                        <div
                            className={cn(
                                'flex gap-3 items-center pt-0 h-[58px]',
                                {
                                    'pt-0': !isBonds && isAdminDeal,
                                }
                            )}
                        >
                            <div className="w-10 h-10 ">
                                <OpportunityLogo
                                    alt={title}
                                    logo={logo}
                                    wrapperClass="w-10 h-10 "
                                    className={'object-contain w-10 h-10 '}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Text
                                    content={
                                        title.length > titleDisplayCharLength
                                            ? `${title.substring(
                                                  0,
                                                  titleDisplayCharLength
                                              )}...`
                                            : title
                                    }
                                    className="text-gray-900 h7-semibold line-clamp-2 font-overused-grotesk"
                                />
                                {isBonds ? (
                                    <Text
                                        className="p6-semibold bg-bonds-closed rounded-[4px] w-fit px-1 py-[2px] text-basicWhite"
                                        content={riskRating}
                                    />
                                ) : (
                                    <Text
                                        content={industryType}
                                        className="text-gray-900 p7-regular"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex  relative items-end gap-8 flex-row w-full min-h-0 h-10">
                        {fields.map(({ id, label, value, className }) =>
                            id === 'minInvestment' && isClosed ? null : (
                                <div
                                    className={cn(
                                        'flex flex-none min-w-22 flex-col first:justify-end w-auto ',
                                        className
                                    )}
                                    key={id}
                                >
                                    <div className="flex flex-row gap-1">
                                        {id === 'yield' ? (
                                            <div>
                                                <Text
                                                    content={
                                                        <>
                                                            <span class="md:h5-semibold">
                                                                {isNaN(
                                                                    parseFloat(
                                                                        value
                                                                    )
                                                                )
                                                                    ? '-'
                                                                    : parseFloat(
                                                                          value
                                                                      ).toFixed(
                                                                          2
                                                                      )}
                                                            </span>
                                                            {!isNaN(
                                                                parseFloat(
                                                                    value
                                                                )
                                                            ) && '%'}
                                                        </>
                                                    }
                                                    className={cn(
                                                        'text-primary-500 text-left h6-semibold '
                                                    )}
                                                />
                                            </div>
                                        ) : (
                                            <Text
                                                content={value}
                                                className={cn(
                                                    'text-primary-500 text-center h6-semibold'
                                                )}
                                            />
                                        )}

                                        {isFestiveDeal && id === 'yield' ? (
                                            <Text
                                                content={preOfferIRR}
                                                className={
                                                    'text-secondary-900 h6-medium opacity-60 text-left self-end line-through'
                                                }
                                            />
                                        ) : null}
                                    </div>
                                    <Text
                                        content={label}
                                        className=" text-gray-500 text-left p7-regular"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const OpportunityInfoStrip = ({
    isClosed,
    interestPayoutFrequency,
    isUpcoming = false,
    isFestiveDeal = false,
}) => {
    if (isClosed || isUpcoming) return null;
    return (
        <div className="flex items-center bg-opp-info-strip rounded-bl-xl w-fit ">
            <div className="relative w-fit py-1.5 px-3 pr-0 border-primary-70 border-b-1 border-l border-r-0 rounded-bl-xl flex items-center gap-2 h-7">
                {!isClosed && (
                    <>
                        <div className="flex items-center gap-1">
                            <Text
                                content={'Interest Payout'}
                                className="text-primary-500 p6-regular"
                            />
                            <Text
                                content={interestPayoutFrequency}
                                className="text-primary-500 p6-semibold"
                            />
                        </div>
                    </>
                )}
            </div>
            <div className={cn('h-full place-content-center')}>
                <CornerElementSvg
                    isFestiveDeal={isFestiveDeal}
                    isAdminDeal={false}
                />
            </div>
        </div>
    );
};

export default OpportunityCarouselCard;
